import React from 'react';
import { X, ShieldCheck, Heart, Maximize2, Home, Layers, Copy, Check, Loader2, ChevronLeft, ChevronRight, Camera } from 'lucide-react';
import { Apartment } from '../types';
import { AGENCY_CONFIG } from '../config/agency';
import { sendClientInquiry } from '../services/inquiryService';

interface PropertyDetailModalProps {
  apartment: Apartment;
  isOpen: boolean;
  onClose: () => void;
  isFavorite: boolean;
  onToggleFavorite: (id: string) => void;
}

export default function PropertyDetailModal({ 
  apartment, 
  isOpen, 
  onClose,
  isFavorite,
  onToggleFavorite
}: PropertyDetailModalProps) {
  const [formSubmitted, setFormSubmitted] = React.useState(false);
  const [formError, setFormError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  // Gallery images list and active selection
  const images = React.useMemo(() => {
    const list: string[] = [];
    if (Array.isArray(apartment.gallery) && apartment.gallery.length > 0) {
      list.push(...apartment.gallery.filter(Boolean));
    } else if (apartment.image) {
      list.push(apartment.image);
    }
    return list.length > 0 ? list : ['https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&fit=crop&q=60'];
  }, [apartment.gallery, apartment.image]);

  const [activeImageIndex, setActiveImageIndex] = React.useState(0);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  React.useEffect(() => {
    setActiveImageIndex(0);
    setIsFullscreen(false);
  }, [apartment.id, isOpen]);

  const handlePrevImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setActiveImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNextImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setActiveImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'ArrowLeft') handlePrevImage();
      if (e.key === 'ArrowRight') handleNextImage();
      if (e.key === 'Escape') {
        if (isFullscreen) {
          setIsFullscreen(false);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isFullscreen, images.length]);

  const currentImage = images[activeImageIndex] || apartment.image;
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    message: `Здравейте, интересувам се от имот с ID: ${apartment.id} (${apartment.title}). Моля да се свържете с мен.`
  });

  if (!isOpen) return null;

  const estateTypeLower = String(apartment.estateType || '').toLowerCase();
  const isPlot = estateTypeLower.includes('парцел') || 
                 estateTypeLower.includes('упи') || 
                 estateTypeLower.includes('земедел') || 
                 estateTypeLower.includes('поземлен') || 
                 estateTypeLower.includes('терен') || 
                 estateTypeLower.includes('нива') || 
                 estateTypeLower.includes('инвест');

  const isGarage = !isPlot && (
    estateTypeLower.includes('гараж') || 
    estateTypeLower.includes('паркомяст')
  );

  const handleCopyId = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(apartment.id);
      } else {
        const tempInput = document.createElement('input');
        tempInput.value = apartment.id;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.phone.trim()) {
      setFormError('Моля, попълнете Вашето име и телефонен номер.');
      return;
    }
    setFormError('');
    setIsSubmitting(true);

    try {
      await sendClientInquiry({
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim() || undefined,
        message: formData.message.trim(),
        propertyId: apartment.id,
        propertyTitle: typeof apartment.title === 'object' ? (apartment.title as any)?.name || 'Имот' : apartment.title,
        source: 'property_modal'
      });
      setFormSubmitted(true);
    } catch (err) {
      setFormError('Възникна грешка при изпращането. Моля, позвънете ни директно по телефона.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const schemaJsonLd = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    "name": apartment.title,
    "description": apartment.description,
    "image": apartment.image,
    "offers": {
      "@type": "Offer",
      "price": apartment.price,
      "priceCurrency": apartment.currency || "EUR",
      "businessFunction": apartment.type === "sale" ? "http://purl.org/goodrelations/v1#Sell" : "http://purl.org/goodrelations/v1#LeaseOut"
    },
    "category": apartment.estateType || "Apartment",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": apartment.city || "Варна",
      "addressRegion": apartment.region || "Варна",
      "streetAddress": apartment.neighborhood || apartment.location
    }
  };

  return (
    <div className="fixed inset-0 bg-[#0F1E32]/75 backdrop-blur-sm flex justify-center items-center z-50 p-4 overflow-y-auto">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaJsonLd).replace(/</g, '\\u003c') }}
      />
      <div className="bg-white rounded-2xl max-w-4xl w-full overflow-hidden shadow-2xl relative animate-fade-in flex flex-col md:flex-row max-h-[90vh]">
        {/* Close Button top corner */}
        <button 
          id="modal-close-btn"
          onClick={onClose}
          className="absolute top-4 right-4 z-25 p-2 rounded-full bg-white/90 hover:bg-white text-gray-700 hover:text-red-500 shadow-md transition-all active:scale-95"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Left Side: Photo panel with Gallery slider & thumbnails */}
        <div className="relative w-full md:w-1/2 bg-slate-950 flex flex-col justify-between overflow-hidden shrink-0 select-none">
          {/* Main Photo Viewport */}
          <div 
            onClick={() => setIsFullscreen(true)}
            className="relative w-full h-72 md:h-full min-h-[300px] md:min-h-[420px] bg-slate-900 overflow-hidden flex items-center justify-center group cursor-zoom-in"
            title="Кликнете за преглед на голям екран"
          >
            <img 
              src={currentImage} 
              alt="" 
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover blur-md scale-110 opacity-30 pointer-events-none"
            />
            <img 
              key={currentImage}
              src={currentImage} 
              alt={apartment.title}
              referrerPolicy="no-referrer"
              className="w-full h-full object-cover relative z-10 transition-all duration-300 group-hover:scale-[1.02]"
              style={{
                transform: `scale(${(apartment.imageZoom !== undefined ? apartment.imageZoom : 100) / 100})`,
                objectPosition: `${apartment.imagePanX !== undefined ? apartment.imagePanX : 50}% ${apartment.imagePanY !== undefined ? apartment.imagePanY : 50}%`,
                transformOrigin: `${apartment.imagePanX !== undefined ? apartment.imagePanX : 50}% ${apartment.imagePanY !== undefined ? apartment.imagePanY : 50}%`
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&fit=crop&q=60';
              }}
            />

            {/* Fullscreen Hint Badge on Hover */}
            <div className="absolute inset-0 bg-black/25 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none z-20">
              <span className="px-3.5 py-1.5 rounded-full bg-black/80 backdrop-blur-md text-white text-xs font-semibold flex items-center gap-1.5 shadow-xl border border-white/20">
                <Maximize2 className="w-3.5 h-3.5 text-[#C5A46E]" />
                Преглед на цял екран
              </span>
            </div>

            {/* Photo Counter Badge */}
            {images.length > 1 && (
              <div className="absolute top-4 right-14 z-20 flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/70 backdrop-blur-md text-white text-xs font-semibold shadow-lg border border-white/10">
                <Camera className="w-3.5 h-3.5 text-[#C5A46E]" />
                <span>{activeImageIndex + 1} / {images.length}</span>
              </div>
            )}

            {/* Prev / Next Navigation Buttons */}
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Предишна снимка"
                  onClick={handlePrevImage}
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/60 hover:bg-[#C5A46E] text-white hover:text-[#0F1E32] flex items-center justify-center transition-all hover:scale-110 active:scale-95 shadow-xl border border-white/10 cursor-pointer"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  aria-label="Следваща снимка"
                  onClick={handleNextImage}
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/60 hover:bg-[#C5A46E] text-white hover:text-[#0F1E32] flex items-center justify-center transition-all hover:scale-110 active:scale-95 shadow-xl border border-white/10 cursor-pointer"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}

            {/* Gradient caption overlay */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent p-6 flex flex-col justify-end z-10 pointer-events-none">
              <span className="text-[10px] text-[#C5A46E] font-bold tracking-widest uppercase mb-1">
                {(() => {
                  const rawN = typeof apartment.neighborhood === 'object' ? (apartment.neighborhood as any)?.name || '' : String(apartment.neighborhood || '');
                  const n = rawN.trim();
                  if (!n) return 'Варна';
                  if (n.toLowerCase().startsWith('с.')) return `${n}, обл. Варна`;
                  if (n.toLowerCase().startsWith('гр.') || n.toLowerCase().includes('варна')) return n;
                  return `${n}, Варна`;
                })()}
              </span>
              <h2 className="text-white text-xl md:text-2xl font-bold font-sans tracking-tight line-clamp-2">
                {typeof apartment.title === 'object' ? (apartment.title as any)?.name || 'Имот' : apartment.title}
              </h2>
            </div>

            {/* Badges and Tags */}
            <div className="absolute top-4 left-4 z-20 flex flex-wrap gap-2 max-w-[70%]">
              <span className={`px-3 py-1 text-xs font-bold tracking-wider rounded text-white shadow-md ${
                apartment.type === 'sale' ? 'bg-[#C5A46E]' : 'bg-[#1D608A]'
              }`}>
                {apartment.type === 'sale' ? 'Оферта за Продажба' : 'Оферта под Наем'}
              </span>
              {Array.isArray(apartment.tags) && apartment.tags.filter(t => {
                const val = typeof t === 'object' ? (t as any)?.name || (t as any)?.title || '' : String(t || '');
                const upper = val.toUpperCase();
                if (upper.includes('ХЕРО') || upper.includes('ГЛАВНА ОФЕРТА')) return false;
                if (upper.includes('АКТ 16') || upper.includes('АКТ16') || upper.includes('ACT 16') || upper.includes('АКТ')) return false;
                return true;
              }).slice(0, 2).map((t, idx) => {
                const val = typeof t === 'object' ? (t as any)?.name || (t as any)?.title || '' : String(t || '');
                if (!val) return null;
                return (
                  <span key={idx} className="px-3 py-1 text-xs font-bold bg-[#0F1E32]/95 text-[#C5A46E] rounded shadow-md uppercase font-sans">
                    {val}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Interactive Thumbnail Ribbon Strip */}
          {images.length > 1 && (
            <div className="bg-slate-900 border-t border-slate-800 p-2.5 flex items-center gap-2 overflow-x-auto shrink-0 scrollbar-thin scrollbar-thumb-slate-700">
              {images.map((imgUrl, idx) => (
                <button
                  key={idx}
                  type="button"
                  aria-label={`Снимка ${idx + 1}`}
                  onClick={() => setActiveImageIndex(idx)}
                  className={`relative shrink-0 w-14 h-11 md:w-16 md:h-12 rounded-lg overflow-hidden transition-all duration-200 border-2 cursor-pointer ${
                    idx === activeImageIndex
                      ? 'border-[#C5A46E] scale-105 shadow-md ring-2 ring-[#C5A46E]/50'
                      : 'border-transparent opacity-60 hover:opacity-100 hover:border-slate-500'
                  }`}
                >
                  <img
                    src={imgUrl}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=150&fit=crop&q=60';
                    }}
                  />
                  {idx === activeImageIndex && (
                    <div className="absolute inset-0 bg-[#C5A46E]/15 pointer-events-none" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right Side: Details and Form Scroll Area */}
        <div className="w-full md:w-1/2 p-6 md:p-8 overflow-y-auto flex flex-col max-h-[500px] md:max-h-[90vh] font-sans">
          {/* Header Pricing */}
          <div className="pb-5 border-b border-gray-100">
            <div className="flex justify-between items-baseline flex-wrap gap-2">
              <span className="text-2xl md:text-3xl font-extrabold text-[#0F1E32]">
                {apartment.type === 'sale' ? (
                  <>€ {apartment.price.toLocaleString('bg-BG')}</>
                ) : (
                  <>{apartment.price.toLocaleString('bg-BG')} € <span className="text-sm font-normal text-gray-500">/ месец</span></>
                )}
              </span>
              <button 
                id="modal-toggle-favorite"
                onClick={() => onToggleFavorite(apartment.id)}
                className="flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-red-500/90 py-1"
              >
                <Heart className={`w-4 h-4 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
                {isFavorite ? 'В Любими' : 'Добави в Любими'}
              </button>
            </div>
            
            <div className="flex items-center gap-2 mt-4 text-xs font-mono text-slate-400">
              <span>Референтен код (ID): {apartment.id}</span>
              <button 
                id="copy-id-btn"
                onClick={handleCopyId}
                className="hover:text-amber-500 focus:outline-none flex items-center gap-1"
                title="Копирай код"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          {/* Home parameters */}
          <div className="grid grid-cols-3 gap-3 my-5 py-3 border-b border-gray-100 bg-slate-50 rounded-xl p-3.5 text-center text-xs text-[#0F1E32]">
            <div className="flex flex-col items-center">
              <Maximize2 className="w-4 h-4 text-[#C5A46E] mb-1" />
              <span className="font-semibold text-[13px]">{apartment.area} m²</span>
              <span className="text-[10px] text-gray-500">Обща площ</span>
            </div>
            <div className="flex flex-col items-center border-x border-gray-200">
              <Home className="w-4 h-4 text-[#C5A46E] mb-1" />
              <span className="font-semibold text-[13px]">
                {isPlot ? 'Парцел' : isGarage ? 'Гараж' : apartment.rooms > 0 ? `${apartment.rooms} стаен` : (apartment.estateType || 'Имот')}
              </span>
              <span className="text-[10px] text-gray-500">{isPlot ? 'Категория' : 'Разпределение'}</span>
            </div>
            <div className="flex flex-col items-center">
              <Layers className="w-4 h-4 text-[#C5A46E] mb-1" />
              <span className="font-semibold text-[13px]">
                {isPlot ? 'Терен' : isGarage ? 'Партер' : (typeof apartment.floor === 'object' ? (apartment.floor as any)?.name || '1' : (apartment.floor || '1'))}
              </span>
              <span className="text-[10px] text-gray-500">{isPlot ? 'Статут' : 'Етаж'}</span>
            </div>
          </div>

          {/* Description Section */}
          <div className="mb-6">
            <h4 className="text-xs font-bold text-slate-400 tracking-wider uppercase mb-2">Описание на имота</h4>
            <p className="text-sm text-slate-700 leading-relaxed font-sans">{typeof apartment.description === 'object' ? (apartment.description as any)?.text || '' : (apartment.description || '')}</p>
          </div>

          {/* Client Request form */}
          <div className="mt-auto bg-slate-50 rounded-xl p-5 border border-slate-100">
            <h4 className="text-xs font-bold text-[#0F1E32] tracking-wider uppercase mb-2 flex items-center gap-1.5 align-middle">
              <ShieldCheck className="w-4 h-4 text-[#C5A46E]" /> Запази час за оглед / Запитай
            </h4>
            <p className="text-xs text-gray-500 mb-4">Нашите опитни брокери ще се свържат с Вас възможно най-скоро с пълна информация.</p>

            {formSubmitted ? (
              <div id="modal-form-success" className="bg-emerald-50 text-emerald-800 p-4 rounded-lg text-xs font-medium space-y-1 my-2">
                <p className="font-bold text-sm">✓ Запитването е изпратено успешно!</p>
                <p>Брокер ще се свърже с Вас на посочения от Вас телефонен номер възможно най-бързо.</p>
              </div>
            ) : (
              <form onSubmit={handleFormSubmit} className="space-y-3">
                <div>
                  <input
                    id="modal-form-name"
                    type="text"
                    required
                    placeholder="Вашето Име и Фамилия *"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full bg-white border border-gray-200 rounded-lg py-2 px-3 text-xs text-[#0F1E32] focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    id="modal-form-phone"
                    type="tel"
                    required
                    placeholder="Телефонен номер *"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full bg-white border border-gray-200 rounded-lg py-2 px-3 text-xs text-[#0F1E32] focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                  />
                  <input
                    id="modal-form-email"
                    type="email"
                    placeholder="Имейл адрес"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full bg-white border border-gray-200 rounded-lg py-2 px-3 text-xs text-[#0F1E32] focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                  />
                </div>
                <div>
                  <textarea
                    id="modal-form-message"
                    rows={2}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full bg-white border border-gray-200 rounded-lg py-2 px-3 text-xs text-[#0F1E32] focus:ring-1 focus:ring-[#C5A46E] focus:outline-none resize-none"
                  />
                </div>
                {formError && (
                  <div className="text-red-500 text-[11px] font-medium">{formError}</div>
                )}
                <button
                  id="modal-submit-btn"
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0F1E32] hover:bg-amber-600 text-white font-bold py-2.5 rounded-lg text-xs tracking-wider uppercase transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Изпращане по имейл...</span>
                    </>
                  ) : (
                    <span>Изпрати запитване по имейл 📨</span>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* ================= FULLSCREEN LIGHTBOX WITH BOTTOM CAROUSEL ================= */}
      {isFullscreen && (
        <div 
          className="fixed inset-0 z-60 bg-black/95 backdrop-blur-md flex flex-col justify-between select-none animate-fade-in"
          onClick={() => setIsFullscreen(false)}
        >
          {/* Top Bar */}
          <div 
            className="flex items-center justify-between px-4 sm:px-8 py-4 bg-gradient-to-b from-black/80 to-transparent z-20 shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col text-left">
              <span className="text-[11px] text-[#C5A46E] font-bold tracking-widest uppercase">
                {(() => {
                  const rawN = typeof apartment.neighborhood === 'object' ? (apartment.neighborhood as any)?.name || '' : String(apartment.neighborhood || '');
                  const n = rawN.trim();
                  const loc = !n ? 'Варна' : n.toLowerCase().startsWith('с.') ? `${n}, обл. Варна` : (n.toLowerCase().startsWith('гр.') || n.toLowerCase().includes('варна')) ? n : `${n}, Варна`;
                  return `${loc} • ID: ${apartment.id}`;
                })()}
              </span>
              <h3 className="text-white text-sm sm:text-base md:text-lg font-bold font-sans line-clamp-1">
                {typeof apartment.title === 'object' ? (apartment.title as any)?.name || 'Имот' : apartment.title}
              </h3>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-mono font-bold">
                <Camera className="w-3.5 h-3.5 text-[#C5A46E]" />
                <span>{activeImageIndex + 1} / {images.length}</span>
              </div>
              <button
                type="button"
                onClick={() => setIsFullscreen(false)}
                className="p-2 sm:px-3 sm:py-2 rounded-xl bg-white/10 hover:bg-white text-white hover:text-slate-950 font-bold text-xs uppercase tracking-wider flex items-center gap-1.5 transition active:scale-95 cursor-pointer shadow-lg"
                title="Затвори (ESC)"
              >
                <X className="w-5 h-5" />
                <span className="hidden sm:inline">Затвори (ESC)</span>
              </button>
            </div>
          </div>

          {/* Center Main Stage */}
          <div 
            className="relative flex-1 flex items-center justify-center p-2 sm:p-6 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Prev Button */}
            {images.length > 1 && (
              <button
                type="button"
                onClick={handlePrevImage}
                aria-label="Предишна снимка"
                className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-black/60 hover:bg-[#C5A46E] text-white hover:text-[#0F1E32] flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 shadow-2xl border border-white/20 cursor-pointer"
              >
                <ChevronLeft className="w-7 h-7" />
              </button>
            )}

            {/* Main high-res image */}
            <div className="relative max-w-full max-h-[72vh] md:max-h-[76vh] flex items-center justify-center">
              <img
                key={currentImage}
                src={currentImage}
                alt={typeof apartment.title === 'object' ? (apartment.title as any)?.name || 'Имот' : apartment.title}
                referrerPolicy="no-referrer"
                className="max-h-[70vh] md:max-h-[75vh] max-w-[92vw] w-auto h-auto object-contain rounded-xl shadow-2xl transition-all duration-300"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200&fit=crop&q=80';
                }}
              />
            </div>

            {/* Next Button */}
            {images.length > 1 && (
              <button
                type="button"
                onClick={handleNextImage}
                aria-label="Следваща снимка"
                className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-black/60 hover:bg-[#C5A46E] text-white hover:text-[#0F1E32] flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 shadow-2xl border border-white/20 cursor-pointer"
              >
                <ChevronRight className="w-7 h-7" />
              </button>
            )}
          </div>

          {/* Bottom Thumbnails Carousel */}
          <div 
            className="w-full bg-black/85 backdrop-blur-md border-t border-white/10 p-3 sm:p-4 z-20 shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-w-5xl mx-auto flex flex-col items-center gap-2">
              <div className="flex items-center gap-2.5 overflow-x-auto max-w-full py-1 px-2 scrollbar-thin scrollbar-thumb-slate-700">
                {images.map((imgUrl, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setActiveImageIndex(idx)}
                    className={`relative shrink-0 w-16 h-12 sm:w-20 sm:h-14 md:w-24 md:h-16 rounded-xl overflow-hidden transition-all duration-200 border-2 cursor-pointer ${
                      idx === activeImageIndex
                        ? 'border-[#C5A46E] scale-105 shadow-xl ring-2 ring-[#C5A46E]'
                        : 'border-white/10 opacity-50 hover:opacity-100 hover:border-white/40'
                    }`}
                  >
                    <img
                      src={imgUrl}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {idx === activeImageIndex && (
                      <div className="absolute inset-0 bg-[#C5A46E]/20 pointer-events-none" />
                    )}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 font-light hidden sm:block">
                Кликнете върху снимка от карусела или използвайте стрелките ⬅️ ➡️ на клавиатурата
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
